'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { getOptimizedImageUrl } from '@/lib/image-utils';

interface Tool {
  id: string;
  databaseId: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText?: string;
    };
  };
  toolCategories?: {
    nodes: ToolCategory[];
  };
  toolFields?: {
    toolTagline?: string;
    toolWebsite?: string;
    toolFeatured?: boolean;
    toolPricingModel?: string;
    toolPricingSummary?: string;
    toolDifficulty?: string;
    toolUseCases?: string[];
    toolPlatforms?: string[];
    toolLogo?: {
      node: {
        sourceUrl: string;
        altText?: string;
        mediaItemUrl?: string;
      };
    } | null;
    toolKeyFeatures?: Array<{
      featureIcon: string;
      featureTitle: string;
      featureDescription: string;
    }>;
    toolStats?: {
      statUsers?: string;
      statRating?: number;
      statCompany?: string;
      statYear?: number;
    };
  };
}

interface ToolCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
}

interface RelatedTool {
  id: string;
  databaseId: number;
  title: string;
  slug: string;
  excerpt: string;
  toolCategories?: {
    nodes: ToolCategory[];
  };
  toolFields?: {
    toolPricingSummary?: string;
  };
}

interface ToolDetailClientProps {
  tool: Tool;
  relatedTools?: RelatedTool[];
}

export default function ToolDetailClient({ tool, relatedTools = [] }: ToolDetailClientProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'pricing'>('overview');
  const pageRef = useScrollAnimation({ threshold: 0.1 });
  
  // Helper function to get category color
  const getCategoryColor = (categoryName: string) => {
    const colors: { [key: string]: string } = {
      'Creating': '#e7131a',
      'Writing': '#1a73e8',
      'Curating': '#34a853',
      'Building': '#fbbc04'
    };
    return colors[categoryName] || '#e7131a';
  };


  // Parse content sections (assuming content has sections marked with headings)
  const parseContent = (content: string) => {
    // This is a simplified parser - in production, you'd want a more robust solution
    const sections = {
      overview: content.split('<h2>')[0] || content,
      features: '',
      pricing: ''
    };
    
    // Try to extract features and pricing sections if they exist
    const contentLower = content.toLowerCase();
    if (contentLower.includes('features')) {
      const featuresMatch = content.match(/<h2[^>]*>.*?features.*?<\/h2>(.*?)(?=<h2|$)/is);
      if (featuresMatch) sections.features = featuresMatch[1];
    }
    if (contentLower.includes('pricing')) {
      const pricingMatch = content.match(/<h2[^>]*>.*?pricing.*?<\/h2>(.*?)(?=<h2|$)/is);
      if (pricingMatch) sections.pricing = pricingMatch[1];
    }
    
    return sections;
  };

  const contentSections = parseContent(tool.content);
  const category = tool.toolCategories?.nodes[0];

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      {/* Hero Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)`,
            }} />
          </div>
          
          <div className="relative z-10 py-20 px-6 sm:px-8 lg:px-12">
            {/* Breadcrumb */}
            <nav className="mb-8">
              <ol className="flex items-center space-x-2 text-sm">
                <li>
                  <Link href="/" className="text-white/60 hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                <li className="text-white/40">/</li>
                <li>
                  <Link href="/tools" className="text-white/60 hover:text-white transition-colors">
                    Tools
                  </Link>
                </li>
                <li className="text-white/40">/</li>
                <li className="text-white">{tool.title}</li>
              </ol>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Column - Content */}
              <div>
                {category && (
                  <span 
                    className="inline-block px-4 py-1 text-white text-xs font-ibm-plex-sans-condensed tracking-wider uppercase rounded-full mb-4"
                    style={{ backgroundColor: getCategoryColor(category.name) }}
                  >
                    {category.name}
                  </span>
                )}
                
                <h1 className="text-[40px] sm:text-[48px] lg:text-[56px] leading-tight text-white mb-6" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
                  {tool.title}
                </h1>
                
                <p className="font-ibm-plex-sans text-[18px] sm:text-[20px] text-white/80 mb-8 leading-relaxed">
                  {tool.excerpt}
                </p>
                
                <div className="flex flex-wrap gap-4">
                  {tool.toolFields?.toolWebsite && (
                    <a 
                      href={tool.toolFields.toolWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="bg-[#e7131a] text-white px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-[#c10e14] hover:opacity-90 hover:shadow-xl inline-block"
                    >
                      TRY {tool.title.toUpperCase()} NOW
                    </a>
                  )}
                  <button className="bg-white/10 backdrop-blur text-white border border-white/20 px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-white/20 hover:border-white/30">
                    WATCH DEMO
                  </button>
                </div>
              </div>

              {/* Right Column - Visual */}
              <div className="relative">
                <div className="relative aspect-square max-w-md mx-auto">
                  {/* Animated background circles */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="absolute w-64 h-64 rounded-full bg-gradient-to-r from-[#e7131a]/20 to-transparent animate-pulse" />
                    <div className="absolute w-48 h-48 rounded-full bg-gradient-to-r from-[#1a73e8]/20 to-transparent animate-pulse animation-delay-2000" />
                    <div className="absolute w-32 h-32 rounded-full bg-gradient-to-r from-[#34a853]/20 to-transparent animate-pulse animation-delay-4000" />
                  </div>
                  
                  {/* Tool Icon/Logo */}
                  <div className="relative z-10 w-full h-full flex items-center justify-center">
                    <div className="w-48 h-48 bg-white/10 backdrop-blur rounded-3xl flex items-center justify-center border border-white/20 shadow-2xl transform hover:opacity-90 transition-transform duration-300 overflow-hidden">
                      {tool.toolFields?.toolLogo?.node?.sourceUrl ? (
                        <Image 
                          src={getOptimizedImageUrl(tool.toolFields.toolLogo.node.sourceUrl, 128, 128)} 
                          alt={tool.toolFields.toolLogo.node.altText || tool.title}
                          width={128}
                          height={128}
                          className="w-32 h-32 object-contain"
                        />
                      ) : (
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
                          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {tool.toolFields?.toolStats && (
        <div className="w-full flex justify-center">
          <div className="relative w-full max-w-[1440px] bg-black/5 backdrop-blur">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-black/10">
              <div className="px-6 py-8 text-center">
                <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{tool.toolFields.toolStats.statUsers || '10M+'}</div>
                <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Active Users</div>
              </div>
              <div className="px-6 py-8 text-center">
                <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{tool.toolFields.toolStats.statRating || '4.8'}</div>
                <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">User Rating</div>
              </div>
              <div className="px-6 py-8 text-center">
                <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{tool.toolFields.toolStats.statCompany || 'OpenAI'}</div>
                <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Company</div>
              </div>
              <div className="px-6 py-8 text-center">
                <div className="text-[32px] text-black mb-1" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{tool.toolFields.toolStats.statYear || '2023'}</div>
                <div className="font-ibm-plex-sans text-[14px] text-black/60 uppercase tracking-wider">Launch Year</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Tabs */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-white">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <div className="px-6 sm:px-8 lg:px-12">
              <nav className="flex space-x-8">
                {(['overview', 'features', 'pricing'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase transition-colors ${
                      activeTab === tab
                        ? 'border-[#e7131a] text-black'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="px-6 sm:px-8 lg:px-12 py-12">
            <div className="max-w-4xl">
              {activeTab === 'overview' && (
                <div className="prose prose-lg max-w-none">
                  <div 
                    className="font-ibm-plex-sans text-[16px] text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: contentSections.overview }}
                  />
                </div>
              )}
              
              {activeTab === 'features' && (
                <div>
                  {tool.toolFields?.toolKeyFeatures && tool.toolFields.toolKeyFeatures.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      {tool.toolFields.toolKeyFeatures.map((feature, index) => {
                        const colors = ['#e7131a', '#1a73e8', '#34a853', '#fbbc04'];
                        const color = colors[index % colors.length];
                        return (
                          <div key={index} className="flex gap-4">
                            <div className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ color }}>
                                <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <div>
                              <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{feature.featureTitle}</h3>
                              <p className="font-ibm-plex-sans text-[14px] text-gray-600">
                                {feature.featureDescription}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : contentSections.features ? (
                    <div 
                      className="prose prose-lg max-w-none"
                      dangerouslySetInnerHTML={{ __html: contentSections.features }}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-[#e7131a]/10 rounded-lg flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#e7131a]">
                            <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Advanced AI Capabilities</h3>
                          <p className="font-ibm-plex-sans text-[14px] text-gray-600">
                            Powered by state-of-the-art models for exceptional results
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-[#1a73e8]/10 rounded-lg flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#1a73e8]">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Easy Integration</h3>
                          <p className="font-ibm-plex-sans text-[14px] text-gray-600">
                            Seamlessly integrate with your existing workflow
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-[#34a853]/10 rounded-lg flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#34a853]">
                            <path d="M3 3H21V21H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M9 9H15V15H9V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Customizable Output</h3>
                          <p className="font-ibm-plex-sans text-[14px] text-gray-600">
                            Fine-tune results to match your specific needs
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-[#fbbc04]/10 rounded-lg flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#fbbc04]">
                            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-[20px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Real-time Processing</h3>
                          <p className="font-ibm-plex-sans text-[14px] text-gray-600">
                            Get instant results with minimal latency
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'pricing' && (
                <div>
                  {tool.toolFields?.toolPricingSummary ? (
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-[20px] text-black mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Pricing Information</h3>
                      <p className="font-ibm-plex-sans text-[16px] text-gray-700 leading-relaxed">
                        {tool.toolFields.toolPricingSummary}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                      <div className="border border-gray-200 rounded-lg p-8 hover:border-[#e7131a] transition-colors">
                        <h3 className="text-[24px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Free</h3>
                        <div className="text-[36px] text-black mb-6" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>$0<span className="text-[16px] font-ibm-plex-sans text-gray-500">/month</span></div>
                        <ul className="space-y-3 mb-8">
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Basic features
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Limited usage
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Community support
                          </li>
                        </ul>
                        <button className="w-full bg-gray-100 text-black px-6 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-gray-200">
                          Get Started
                        </button>
                      </div>
                      
                      <div className="border-2 border-[#e7131a] rounded-lg p-8 relative">
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-[#e7131a] text-white px-4 py-1 text-xs font-ibm-plex-sans-condensed tracking-wider uppercase rounded-full">
                          Popular
                        </div>
                        <h3 className="text-[24px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Pro</h3>
                        <div className="text-[36px] text-black mb-6" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>$20<span className="text-[16px] font-ibm-plex-sans text-gray-500">/month</span></div>
                        <ul className="space-y-3 mb-8">
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            All features
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Unlimited usage
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Priority support
                          </li>
                        </ul>
                        <button className="w-full bg-[#e7131a] text-white px-6 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-[#c10e14] hover:opacity-90">
                          Start Free Trial
                        </button>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-8 hover:border-[#e7131a] transition-colors">
                        <h3 className="text-[24px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Enterprise</h3>
                        <div className="text-[36px] text-black mb-6" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Custom</div>
                        <ul className="space-y-3 mb-8">
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Custom features
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Dedicated support
                          </li>
                          <li className="font-ibm-plex-sans text-[14px] text-gray-600 flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            SLA guarantee
                          </li>
                        </ul>
                        <button className="w-full bg-gray-100 text-black px-6 py-3 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-gray-200">
                          Contact Sales
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Related Tools Section */}
      {relatedTools.length > 0 && (
        <div className="w-full flex justify-center">
          <div className="relative w-full max-w-[1440px] bg-[#f6f4f1] py-16 px-6 sm:px-8 lg:px-12">
            <h2 className="text-[32px] text-black mb-8 text-center" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>Related Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedTools.map((relatedTool) => (
                <Link
                  key={relatedTool.id}
                  href={`/tools/${relatedTool.slug}`}
                  className="bg-white p-6 border border-gray-200 hover:border-[#e7131a] transition-all duration-300 hover:shadow-soft cursor-pointer block"
                >
                  <div className="w-12 h-12 bg-[#f6f4f1] rounded-lg mb-4 flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-gray-400">
                      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-[18px] text-black mb-2" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>{relatedTool.title}</h3>
                  <p className="font-ibm-plex-sans text-[14px] text-gray-600 mb-4 line-clamp-2">
                    {relatedTool.excerpt}
                  </p>
                  {relatedTool.toolFields?.toolPricingSummary && (
                    <p className="font-ibm-plex-sans text-[12px] text-gray-500 mb-2">
                      {relatedTool.toolFields.toolPricingSummary}
                    </p>
                  )}
                  <span className="font-ibm-plex-sans-condensed text-[12px] tracking-wider uppercase text-[#e7131a]">
                    EXPLORE →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <div className="w-full flex justify-center">
        <div className="relative w-full max-w-[1440px] bg-gradient-to-r from-[#e7131a] to-[#c10e14] py-16 px-6 sm:px-8 lg:px-12">
          <div className="text-center">
            <h2 className="text-[36px] text-white mb-4" style={{ fontFamily: 'var(--font-gilda-display), serif' }}>
              Ready to Transform Your Workflow?
            </h2>
            <p className="font-ibm-plex-sans text-[18px] text-white/80 mb-8 max-w-2xl mx-auto">
              Join millions of users who are already creating amazing things with {tool.title}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button className="bg-white text-[#e7131a] px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-gray-100 hover:opacity-90 hover:shadow-xl">
                START FREE TRIAL
              </button>
              <button className="bg-white/10 backdrop-blur text-white border border-white/20 px-8 py-4 font-ibm-plex-sans-condensed text-[14px] tracking-wider uppercase rounded-lg transition-all duration-300 hover:bg-white/20 hover:border-white/30">
                VIEW PRICING
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}